import {
  exclusiveOfferDetailDataSchema,
  type ExclusiveOfferDetailData
} from '@/models/api/exclusive-offer-detail-data'
import {
  exclusiveOfferListDataSchema,
  type ExclusiveOfferListData
} from '@/models/api/exclusive-offer-list-data'
import type { GetCityByLocationRequest } from '@/models/api/get-city-by-location-request'
import {
  getCityByLocationResponseSchema,
  type GetCityByLocationResponse
} from '@/models/api/get-city-by-location-response'
import type { GetExclusiveOfferListRequest } from '@/models/api/get-exclusive-offer-list-request'
import type { GetExclusiveOfferTypeListRequest } from '@/models/api/get-exclusive-offer-type-list-request'
import {
  getExclusiveOfferTypeListResponseSchema,
  type GetExclusiveOfferTypeListResponse
} from '@/models/api/get-exclusive-offer-type-list-response'
import type { ListRequestCommon } from '@/models/api/list-request-common'
import type { News } from '@/models/api/news'
import { newsSchema } from '@/models/api/news'
import {
  areaCountryAirportSchema,
  type AreaCountryAirport
} from '@library/models/area-country-airport'
import {
  areaCountryCitySchema,
  type AreaCountryCity
} from '@library/models/area-country-city'
import {
  apiValidator,
  type ApiFactory,
  type ApiRequestConfig,
  type ApiResponse
} from '~library/services'

export default class NewsApi {
  constructor(private api: ApiFactory) {}

  /**
   * API 取得最新消息明細
   * https://hackmd.io/Me823ObzR36yQFl1FH9eYA
   */
  @apiValidator(newsSchema)
  getData(id: string, config?: ApiRequestConfig): Promise<ApiResponse<News>> {
    return this.api.get(`News/News/${id}`, config)
  }

  /**
   * API 取得最新消息清單
   * https://hackmd.io/F08y8-WMSVmBNp5QWaxrOg
   */
  @apiValidator(newsSchema.array())
  getList(
    query: ListRequestCommon,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<News[]>> {
    return this.api.post('News/GetList', query, config)
  }

  /**
   * API 取得異業合作清單
   * https://hackmd.io/9RAo3DPwSimA64SY0CtYbA
   */
  @apiValidator(newsSchema.array())
  getJointPromotionList(
    query: ListRequestCommon,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<News[]>> {
    return this.api.post('News/GetJointPromotionList', query, config)
  }

  /**
   * API 取得異業合作單筆資料
   * https://hackmd.io/YbBwewDJQZOnUbT7V5h45Q
   */
  @apiValidator(newsSchema)
  getJointPromotionDetail(
    id: string,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<News>> {
    return this.api.get(`News/GetJointPromotionDetail/${id}`, config)
  }

  /**
   * API 取得獨享清單
   * https://hackmd.io/RwPBmlnASfOWLIlNDEAfAw
   */
  @apiValidator(newsSchema.array())
  getExclusiveList(
    query: ListRequestCommon,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<News[]>> {
    return this.api.post('News/GetExclusiveList', query, config)
  }

  /**
   * API 取得獨享單筆資料
   * https://hackmd.io/M95Rr6gVSWWFaMnRaRgmWg
   */
  @apiValidator(newsSchema)
  getExclusiveDetail(
    id: string,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<News>> {
    return this.api.get(`News/GetExclusiveDetail/${id}`, config)
  }

  /**
   * API 取得會員尊享清單
   * https://hackmd.io/yVfI4BUmR3Ksr0JCDXjLCQ
   */
  @apiValidator(exclusiveOfferListDataSchema.array())
  getExclusiveOfferList(
    query: GetExclusiveOfferListRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<ExclusiveOfferListData[]>> {
    return this.api.post('News/GetExclusiveOfferList', query, config)
  }

  /**
   * API 取得會員尊享明細資料
   * https://hackmd.io/K_cVxuFoT6WD9LIQquZ-KQ
   */
  @apiValidator(exclusiveOfferDetailDataSchema)
  getExclusiveOfferDetail(
    query: { id: string },
    config?: ApiRequestConfig
  ): Promise<ApiResponse<ExclusiveOfferDetailData>> {
    return this.api.post('News/GetExclusiveOfferDetail', query, config)
  }

  /**
   * API 取得定位城市
   * https://hackmd.io/Mejql0lASnaS48JKRobryQ
   */
  @apiValidator(getCityByLocationResponseSchema)
  getCityByLocation(
    query: GetCityByLocationRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<GetCityByLocationResponse>> {
    return this.api.post('News/GetCityByLocation', query, config)
  }

  /**
   * API 取得商品分類選項
   * https://hackmd.io/Ro2YzllBSv-4PjaJmxq6ew
   */
  @apiValidator(getExclusiveOfferTypeListResponseSchema)
  getExclusiveOfferTypeList(
    query: GetExclusiveOfferTypeListRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<GetExclusiveOfferTypeListResponse>> {
    return this.api.post('News/GetExclusiveOfferTypeList', query, config)
  }

  /**
   * API 取得區域、國家與航點對應清單
   * https://hackmd.io/fP98pSPYRF2nynYmKpjM4Q
   */
  @apiValidator(areaCountryAirportSchema.array())
  getAreaCountryAirport(
    config?: ApiRequestConfig
  ): Promise<ApiResponse<AreaCountryAirport[]>> {
    return this.api.get(`News/GetAreaCountryAirport`, config)
  }

  /**
   * API 取得區域、國家與城市對應清單
   * https://hackmd.io/fP98pSPYRF2nynYmKpjM4Q
   */
  @apiValidator(areaCountryCitySchema.array())
  getAreaCountryCity(
    config?: ApiRequestConfig
  ): Promise<ApiResponse<AreaCountryCity[]>> {
    return this.api.get(`News/GetAreaCountryCity`, config)
  }
}
